import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { FileText, Download, Send, RefreshCw, ExternalLink } from 'lucide-react';

const AdminInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchBizId, setSearchBizId] = useState('');

  useEffect(() => { fetchInvoices(); }, []);

  const fetchInvoices = async (businessId = null) => {
    setLoading(true);
    try {
      const params = businessId ? `?business_id=${businessId}&limit=50` : '?limit=50';
      const res = await axiosInstance.get(`/stripe/admin/invoices${params}`);
      setInvoices(res.data.invoices || []);
    } catch (err) {
      console.error('Invoice fetch error:', err);
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to load invoices' });
    } finally {
      setLoading(false);
    }
  };

  const resendInvoice = async (invoiceId) => {
    setResending(invoiceId);
    try {
      const res = await axiosInstance.post(`/stripe/admin/invoices/${invoiceId}/resend`);
      setMessage({ type: 'success', text: res.data.message });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to resend' });
    } finally {
      setResending(null);
      setTimeout(() => setMessage(null), 5000);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const statusColor = (status) => {
    const map = {
      paid: 'bg-green-100 text-green-800',
      open: 'bg-blue-100 text-blue-800',
      draft: 'bg-gray-100 text-gray-800',
      void: 'bg-red-100 text-red-800',
      uncollectible: 'bg-red-100 text-red-800',
    };
    return map[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="bg-white rounded-lg shadow mb-6" data-testid="invoices-tab">
      <div className="p-4 border-b flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <FileText className="h-5 w-5 text-purple-600" />
          Subscription Invoices
        </h2>
        <button
          onClick={() => fetchInvoices()}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-200"
          data-testid="refresh-invoices-btn"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {message && (
        <div className={`mx-4 mt-3 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-gray-500">Loading invoices from Stripe...</div>
      ) : invoices.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
          <p>No invoices found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invoices-table">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium text-gray-600">Invoice #</th>
                <th className="px-4 py-3 font-medium text-gray-600">Customer</th>
                <th className="px-4 py-3 font-medium text-gray-600">Date</th>
                <th className="px-4 py-3 font-medium text-gray-600">Amount</th>
                <th className="px-4 py-3 font-medium text-gray-600">Tax</th>
                <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {invoices.map((inv) => (
                <tr key={inv.id} className="hover:bg-gray-50" data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{inv.number || inv.id.slice(-8)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{inv.customer_name || '-'}</div>
                    <div className="text-xs text-gray-500">{inv.customer_email}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(inv.created)}</td>
                  <td className="px-4 py-3 font-medium">{inv.total.toFixed(2)} {inv.currency.toUpperCase()}</td>
                  <td className="px-4 py-3 text-gray-500">{inv.tax > 0 ? `${inv.tax.toFixed(2)}` : '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColor(inv.status)}`}>
                      {inv.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      {inv.invoice_pdf && (
                        <a
                          href={inv.invoice_pdf}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-500 hover:text-purple-600 hover:bg-purple-50 rounded"
                          title="Download PDF"
                          data-testid={`invoice-pdf-${inv.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                      )}
                      {inv.hosted_invoice_url && (
                        <a
                          href={inv.hosted_invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="View online"
                          data-testid={`invoice-view-${inv.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => resendInvoice(inv.id)}
                        disabled={resending === inv.id}
                        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded disabled:opacity-50"
                        title="Resend invoice"
                        data-testid={`invoice-resend-${inv.id}`}
                      >
                        <Send className={`h-4 w-4 ${resending === inv.id ? 'animate-pulse' : ''}`} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminInvoices;
